import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { 
  ClipboardList, User, Mail, Phone, Activity, Clock,
  CheckCircle, AlertCircle, RefreshCw, Edit, Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminSubUserTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: '',
    priority: '',
    assigned_to_admin: '',
    admin_notes: ''
  });

  useEffect(() => {
    fetchAllTickets();
  }, []);

  const fetchAllTickets = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/monitoring/subuser/tickets/admin/all-tickets`
      );
      setTickets(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
      setLoading(false);
    }
  };

  const handleSelectTicket = async (ticket) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/monitoring/subuser/tickets/admin/ticket/${ticket.id}`
      );
      setSelectedTicket(response.data);
      setUpdateData({
        status: response.data.status,
        priority: response.data.priority,
        assigned_to_admin: response.data.assigned_to_admin || '',
        admin_notes: ''
      });
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error('Failed to load ticket details');
    }
  };

  const handleUpdateTicket = async () => {
    try {
      await axios.put(
        `${API_URL}/api/monitoring/subuser/tickets/admin/update/${selectedTicket.id}`,
        updateData,
        { params: { admin_username: 'admin' } }
      );

      toast.success('Ticket updated successfully');
      setEditMode(false);
      fetchAllTickets();
      handleSelectTicket(selectedTicket);
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update ticket');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Sub-User Tickets</h1>
          <p className="text-gray-600 mt-1">Manage tickets created by monitoring sub-users</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  All Tickets ({tickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto">
                {tickets.map((ticket) => (
                  <Card
                    key={ticket.id}
                    className={`cursor-pointer hover:shadow-md transition-shadow ${
                      selectedTicket?.id === ticket.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => handleSelectTicket(ticket)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-mono text-xs text-gray-600">{ticket.ticket_number}</span>
                        <Badge className={getStatusColor(ticket.status)} size="sm">
                          {ticket.status}
                        </Badge>
                      </div>
                      <h4 className="font-semibold text-sm mb-1">{ticket.subject}</h4>
                      <p className="text-xs text-gray-600">By: {ticket.created_by_sub_user_name}</p>
                      <p className="text-xs text-gray-600">Customer: {ticket.customer_name}</p>
                    </CardContent>
                  </Card>
                ))}
                {tickets.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>No tickets found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Ticket Details */}
          <div className="lg:col-span-2">
            {selectedTicket ? (
              <div className="space-y-6">
                {/* Ticket Header */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-3">
                        <span>{selectedTicket.ticket_number}</span>
                        <Badge className={getStatusColor(selectedTicket.status)}>
                          {selectedTicket.status}
                        </Badge>
                        <Badge className={getPriorityColor(selectedTicket.priority)}>
                          {selectedTicket.priority}
                        </Badge>
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditMode(!editMode)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {editMode ? 'Cancel' : 'Edit'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold">{selectedTicket.subject}</h3>
                      <p className="text-gray-600 mt-2">{selectedTicket.description}</p>
                    </div>

                    {/* Customer Info */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-3">Customer Information</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Name:</span>
                          <p className="font-medium">{selectedTicket.customer_name}</p>
                        </div>
                        {selectedTicket.customer_email && (
                          <div>
                            <span className="text-gray-600">Email:</span>
                            <p className="font-medium">{selectedTicket.customer_email}</p>
                          </div>
                        )}
                        {selectedTicket.customer_mobile && (
                          <div>
                            <span className="text-gray-600">Mobile:</span>
                            <p className="font-medium">{selectedTicket.customer_mobile}</p>
                          </div>
                        )}
                        {selectedTicket.customer_ip && (
                          <div>
                            <span className="text-gray-600">IP Address:</span>
                            <p className="font-medium">{selectedTicket.customer_ip}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sub-User Info */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2">Created By Sub-User</h4>
                      <div className="text-sm space-y-1">
                        <p><span className="text-gray-600">Name:</span> <span className="font-medium">{selectedTicket.created_by_sub_user_name}</span></p>
                        <p><span className="text-gray-600">Email:</span> <span className="font-medium">{selectedTicket.created_by_sub_user_email}</span></p>
                        <p><span className="text-gray-600">Created:</span> <span className="font-medium">{new Date(selectedTicket.created_at).toLocaleString()}</span></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Update Form */}
                {editMode && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Edit className="h-5 w-5" />
                        Update Ticket
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select
                            value={updateData.status}
                            onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Priority</Label>
                          <Select
                            value={updateData.priority}
                            onValueChange={(value) => setUpdateData({ ...updateData, priority: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Assign To Admin</Label>
                        <Input
                          value={updateData.assigned_to_admin}
                          onChange={(e) => setUpdateData({ ...updateData, assigned_to_admin: e.target.value })}
                          placeholder="Admin username"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Admin Notes</Label>
                        <Textarea
                          value={updateData.admin_notes}
                          onChange={(e) => setUpdateData({ ...updateData, admin_notes: e.target.value })}
                          placeholder="Add notes about this update..."
                          rows={3}
                        />
                      </div>

                      <Button
                        onClick={handleUpdateTicket}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Activity Log */}
                {selectedTicket.activity_log && selectedTicket.activity_log.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        Activity Log
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedTicket.activity_log.map((activity, index) => (
                          <div key={index} className="flex gap-3 pb-3 border-b last:border-0">
                            <div className="mt-1">
                              <CheckCircle className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{activity.performed_by}</span>
                                <Badge variant="outline" className="text-xs">
                                  {activity.performed_by_type}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600">{activity.details}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(activity.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <ClipboardList className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Select a ticket to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubUserTickets;
